import type React from 'react';
import type { RefObject } from 'react';

interface Props {
    wrapperRef: RefObject<HTMLDivElement | null>;
    playerRef?: RefObject<HTMLVideoElement | null>;
    duration: number;
    setPlayedSeconds: (playedSeconds: number) => void;
}

interface SeekVideoProps extends Props {
    newTime: number;
}

interface PerformSeekProps extends Props {
    target: number;
    isYouTube: boolean;
}

interface PlayerKeyboardProps {
    settingsOpen: boolean;
    volumeMenuOpen: boolean;
    canSkip: boolean;
    allowSpeedChange: boolean;
    enableSubtitles: boolean;
    duration: number;
    volume: number;
    setVolume: (v: number) => void;
    prevVolumeRef: RefObject<number>;
    playbackRate: number;
    setPlaybackRate: (r: number) => void;
    togglePlay: () => void;
    toggleFullscreen: () => void;
    toggleSubtitles: () => void;
    skipForward: (by: number) => void; // player handles clamping/guards inside
    skipBack: (by: number) => void; // player handles clamping/guards inside
    seekTo: (target: number) => void; // player handles canSkip and seek implementation

    // available speeds (e.g., SPEEDS)
    speeds: number[];
}

interface ControlsAutoHideProps {
    playing: boolean;
    settingsOpen: boolean;
    volumeMenuOpen: boolean;
    isScrubbing: boolean;
    setControlsVisible: (v: boolean) => void;
    hideControlsTimeout: RefObject<ReturnType<typeof setTimeout> | null>;
    delay?: number;
}

export const getNormalizedDuration = (value: number | null | undefined): number =>
    Number.isFinite(value) && Number(value) > 0 ? Number(value) : 0;

// format time for watermark
export const formatTime = (sec: number) => {
    if (!Number.isFinite(sec) || sec <= 0) {
        return '0:00';
    }
    const minutes = Math.floor(sec / 60);
    const seconds = Math.floor(sec % 60)
        .toString()
        .padStart(2, '0');
    return `${minutes}:${seconds}`;
};

// get the current time of the video
const seekVideo = ({ newTime, wrapperRef, playerRef, duration, setPlayedSeconds }: SeekVideoProps) => {
    const clamped = duration ? Math.max(0, Math.min(newTime, duration)) : Math.max(0, newTime);
    setPlayedSeconds(clamped);

    const videoEl = playerRef?.current ?? wrapperRef.current?.querySelector('video');
    if (videoEl) videoEl.currentTime = clamped;
};

// get the YouTube tag from DOM
const getYouTubeElement = (wrapperRef: RefObject<HTMLDivElement | null>): HTMLVideoElement | null => {
    return wrapperRef.current?.querySelector('youtube-video') as HTMLVideoElement | null;
};

export const performSeek = ({
    target,
    isYouTube,
    wrapperRef,
    playerRef,
    duration,
    setPlayedSeconds,
}: PerformSeekProps) => {
    const safeDuration = getNormalizedDuration(duration);
    const clamped = safeDuration ? Math.max(0, Math.min(target, safeDuration)) : Math.max(0, target);

    if (isYouTube) {
        const element = getYouTubeElement(wrapperRef) ?? playerRef?.current;
        if (element) element.currentTime = clamped;

        setPlayedSeconds(clamped);
        return;
    }

    seekVideo({ newTime: clamped, wrapperRef, playerRef, duration: safeDuration, setPlayedSeconds });
};

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

export const createPlayerKeyboardHandler = (opts: PlayerKeyboardProps) => (e: React.KeyboardEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    const tag = target.tagName.toLowerCase();

    // allow keys when focused on the range slider
    if (tag === 'input') {
        const input = target as HTMLInputElement;
        if (input.type !== 'range') return;
    } else if (['textarea', 'select', 'button'].includes(tag) || target.isContentEditable) {
        return;
    }

    const {
        settingsOpen,
        volumeMenuOpen,
        canSkip,
        allowSpeedChange,
        enableSubtitles,
        duration,
        volume,
        setVolume,
        prevVolumeRef,
        playbackRate,
        setPlaybackRate,
        togglePlay,
        toggleFullscreen,
        toggleSubtitles,
        skipForward,
        skipBack,
        seekTo,
        speeds,
    } = opts;

    const guardArrows = settingsOpen || volumeMenuOpen;

    const changeVolume = (delta: number) => {
        const next = clamp(volume + delta, 0, 1);
        if (next > 0) prevVolumeRef.current = next;
        setVolume(next);
    };

    const toggleMute = () => {
        if (volume === 0) setVolume(prevVolumeRef.current || 1);
        else {
            prevVolumeRef.current = volume || 1;
            setVolume(0);
        }
    };

    const stepSpeed = (dir: 1 | -1) => {
        if (!allowSpeedChange || !canSkip) return;
        const idx = speeds.findIndex((s) => s === playbackRate);
        const next = speeds[clamp(idx + dir, 0, speeds.length - 1)];
        setPlaybackRate(next);
    };

    switch (e.key) {
        case ' ':
            e.preventDefault();
            togglePlay();
            break;

        case 'ArrowRight':
            if (!guardArrows) {
                e.preventDefault();
                skipForward(5);
            }
            break;
        case 'ArrowLeft':
            if (!guardArrows) {
                e.preventDefault();
                skipBack(5);
            }
            break;
        case 'ArrowUp':
            if (!guardArrows) {
                e.preventDefault();
                changeVolume(+0.05);
            }
            break;
        case 'ArrowDown':
            if (!guardArrows) {
                e.preventDefault();
                changeVolume(-0.05);
            }
            break;

        case 'm':
        case 'M':
            e.preventDefault();
            toggleMute();
            break;

        case 'f':
        case 'F':
            e.preventDefault();
            toggleFullscreen();
            break;

        case 'c':
        case 'C':
            if (enableSubtitles) {
                e.preventDefault();
                toggleSubtitles();
            }
            break;

        case '>':
        case '.':
            e.preventDefault();
            stepSpeed(+1);
            break;
        case '<':
        case ',':
            e.preventDefault();
            stepSpeed(-1);
            break;

        case '0':
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9':
            if (canSkip) {
                e.preventDefault();
                const pct = Number(e.key) / 10; // 0 -> 0%, 9 -> 90%
                seekTo(duration * pct);
            }
            break;

        default:
            break;
    }
};

export const runControlsAutoHide = ({
    playing,
    settingsOpen,
    volumeMenuOpen,
    isScrubbing,
    setControlsVisible,
    hideControlsTimeout,
    delay = 2500,
}: ControlsAutoHideProps) => {
    if (!playing) {
        setControlsVisible(true);
        if (hideControlsTimeout.current) {
            clearTimeout(hideControlsTimeout.current);
            hideControlsTimeout.current = null;
        }
    } else {
        if (hideControlsTimeout.current) {
            clearTimeout(hideControlsTimeout.current);
        }
        setControlsVisible(true);
        if (!settingsOpen && !volumeMenuOpen && !isScrubbing) {
            hideControlsTimeout.current = setTimeout(() => {
                setControlsVisible(false);
            }, delay);
        }
    }

    return () => {
        if (hideControlsTimeout.current) {
            clearTimeout(hideControlsTimeout.current);
            hideControlsTimeout.current = null;
        }
    };
};

export const revealControls = ({
    playing,
    settingsOpen,
    volumeMenuOpen,
    isScrubbing,
    setControlsVisible,
    hideControlsTimeout,
    delay = 2500,
}: ControlsAutoHideProps) => {
    if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
        hideControlsTimeout.current = null;
    }
    setControlsVisible(true);
    if (playing && !settingsOpen && !volumeMenuOpen && !isScrubbing) {
        hideControlsTimeout.current = setTimeout(() => {
            setControlsVisible(false);
        }, delay);
    }
};

const getYouTubeId = (url: string) => {
    // extract YT id from full URL or short url version (youtu.be)
    const media = url.match(/(?:v=|\/)([0-9A-Za-z_-]{11})(?:[?&].*)?$/) || url.match(/youtu\.be\/([0-9A-Za-z_-]{11})/);

    return media?.[1] ?? null;
};

// if image is not available YT will return thumbnail so we want to catch this case and later use first available fallback img
const verifyThumbnail = (
    url: string,
    { minWidth = 1, minHeight = 1 }: { minWidth?: number; minHeight?: number } = {},
) =>
    new Promise<{ exists: boolean; url: string; width: number; height: number }>((resolve) => {
        const img = new Image();
        img.onload = () => {
            const exists = img.naturalWidth >= minWidth && img.naturalHeight >= minHeight;
            resolve({ exists, url, width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => resolve({ exists: false, url, width: 0, height: 0 });
        img.src = url;
    });

export const getVideoCover = async (isYouTube: boolean, cover: string | null, url: string): Promise<string | null> => {
    if (!isYouTube) return cover;

    const youTubeId = getYouTubeId(url);
    if (!youTubeId) return cover;

    // gets YT thumb based on video id
    // will load max resolution img first and first available fallback if the former is missing
    // names must be hardcoded like that because they come from API
    const base = `https://img.youtube.com/vi/${youTubeId}`;
    const thumbnails: { url: string; minW: number; minH: number }[] = [
        { url: `${base}/maxresdefault.jpg`, minW: 1280, minH: 720 },
        { url: `${base}/sddefault.jpg`, minW: 640, minH: 480 },
        { url: `${base}/hqdefault.jpg`, minW: 480, minH: 360 },
        { url: `${base}/mqdefault.jpg`, minW: 320, minH: 180 },
        { url: `${base}/0.jpg`, minW: 120, minH: 90 },
    ];

    for (const thumbnail of thumbnails) {
        const res = await verifyThumbnail(thumbnail.url, { minWidth: thumbnail.minW, minHeight: thumbnail.minH });
        if (res.exists) return res.url;
    }

    return cover;
};

/** Audio track data for the settings menu (from the HLS master playlist: NAME and LANGUAGE). */
export interface AudioTrackItem {
    id: string;
    label: string;
    language: string;
}

// The app locale for Norwegian is "no", but HLS streams use "nb" (Bokmål) or "nn" (Nynorsk)
const LANGUAGE_ALIASES: Partial<Record<string, string>> = {
    nb: 'no',
    nn: 'no',
};

// "en-GB" -> "en", "NB" -> "no"
export const normalizeLanguage = (value: string | null | undefined): string => {
    const [primarySubtag = ''] = (value ?? '').trim().toLowerCase().split(/[-_]/);

    return LANGUAGE_ALIASES[primarySubtag] ?? primarySubtag;
};

// The language code is the stable key. The label is only a fallback for tracks without LANGUAGE.
export const getAudioTrackKey = (track: AudioTrackItem): string => track.language || track.label;

export const getAudioTrackLabel = (track: AudioTrackItem): string => track.label || track.language;

let englishLanguageNames: Intl.DisplayNames | null = null;

// "nb" -> "Norwegian Bokmål": English names, like NAME in the Bunny.net playlist. Unknown values are shown as is.
export const getLanguageDisplayName = (language: string): string => {
    try {
        englishLanguageNames ??= new Intl.DisplayNames(['en'], { type: 'language' });

        return englishLanguageNames.of(language) ?? language;
    } catch {
        return language;
    }
};

/**
 * Returns the first track that matches one of the keys (checked in the given order).
 * Returns undefined when nothing matches, so the stream default (DEFAULT=YES) stays selected.
 */
export const findPreferredAudioTrack = <T extends AudioTrackItem>(
    tracks: T[],
    preferredKeys: (string | null | undefined)[],
): T | undefined => {
    for (const key of preferredKeys) {
        const normalizedKey = normalizeLanguage(key);

        if (normalizedKey) {
            const match = tracks.find((track) => normalizeLanguage(getAudioTrackKey(track)) === normalizedKey);

            if (match) {
                return match;
            }
        }
    }

    return undefined;
};
